import type { ParsedFile } from '@/features/parties/import/lib/types';
import { Button, SelectInput, type SelectInputOption } from '@my/ui';
import type { EquipmentOption } from '../lib/equipment';
import {
  fieldsForType,
  TYPE_LABEL,
  type ImportResourceType,
  type ResourceColumnMapping,
  type ResourceField,
  type ResourceFieldDef,
  type ResourceImportDefaults,
} from '../lib/types';

interface MappingStepProps {
  parsed: ParsedFile;
  type: ImportResourceType;
  mapping: ResourceColumnMapping;
  defaults: ResourceImportDefaults;
  equipmentOptions: EquipmentOption[];
  onMappingChange: (next: ResourceColumnMapping) => void;
  onDefaultsChange: (next: ResourceImportDefaults) => void;
  onBack: () => void;
  onContinue: () => void;
}

const IGNORE = '__ignore__';

/**
 * Step 3 — map source columns to the fields valid for the chosen resource type.
 * Only Name is required; everything else is optional. Columns left unmapped are
 * ignored, which is how fleet extras (make, model, chassis number…) are handled
 * — the Resource record has nowhere to store them.
 */
export function MappingStep({
  parsed,
  type,
  mapping,
  defaults,
  equipmentOptions,
  onMappingChange,
  onDefaultsChange,
  onBack,
  onContinue,
}: MappingStepProps) {
  const fields = fieldsForType(type);
  const headerOptions: SelectInputOption[] = [
    { value: IGNORE, label: '— ignore —' },
    ...parsed.headers.map((h) => ({ value: h, label: h })),
  ];

  const setField = (field: ResourceField, value: string) => {
    const next: ResourceColumnMapping = { ...mapping };
    if (value === IGNORE || value === '') delete next[field];
    else next[field] = value;
    onMappingChange(next);
  };

  const sample = (field: ResourceField): string | null => {
    const header = mapping[field];
    if (!header) return null;
    const v = parsed.rows[0]?.[header];
    return v && v.trim() ? v.trim() : null;
  };

  const requiredMissing = fields.filter((f) => f.required && !mapping[f.id]).map((f) => f.label);
  const canContinue = requiredMissing.length === 0;

  const mappedHeaders = new Set(Object.values(mapping));
  const ignoredHeaders = parsed.headers.filter((h) => !mappedHeaders.has(h));

  const renderRow = (f: ResourceFieldDef) => {
    const s = sample(f.id);
    return (
      <div key={f.id} className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[1fr_1.2fr]">
        <div className="pt-2">
          <span className="text-sm font-medium text-slate-700">{f.label}</span>
          {f.required && <span className="ml-0.5 text-red-500">*</span>}
          {f.hint && <p className="text-[11px] italic text-slate-400">{f.hint}</p>}
        </div>
        <div>
          <SelectInput
            label="Source column"
            name={`map-${f.id}`}
            value={mapping[f.id] ?? IGNORE}
            options={headerOptions}
            onChange={(e) => setField(f.id, e.target.value)}
          />
          {s && (
            <p className="ml-1 mt-0.5 truncate text-[11px] text-slate-500" title={s}>
              e.g. <span className="font-mono">{s}</span>
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Batch defaults */}
      <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Applies to every imported row
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <SelectInput
            label="Resource type"
            name="def-resource-type"
            value={type}
            disabled
            options={[{ value: type, label: TYPE_LABEL[type] }]}
            onChange={() => undefined}
          />
          <SelectInput
            label="Status"
            name="def-status"
            value={defaults.status}
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' },
            ]}
            onChange={(e) =>
              onDefaultsChange({ ...defaults, status: e.target.value as ResourceImportDefaults['status'] })
            }
          />
        </div>
        {equipmentOptions.length === 0 && (type === 'Trailer' || type === 'Rigid') && (
          <p className="text-[11px] text-amber-600">
            No {type.toLowerCase()} equipment types are set up for this organisation — rows will import
            without one. Add them in Settings → Central Configuration.
          </p>
        )}
      </div>

      {/* Column mapping */}
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {TYPE_LABEL[type]} fields
        </p>
        <div className="grid grid-cols-1 gap-x-8 gap-y-3 lg:grid-cols-2">{fields.map(renderRow)}</div>
      </div>

      {ignoredHeaders.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Ignored columns ({ignoredHeaders.length})
          </p>
          <p className="mb-1.5 text-[11px] italic text-slate-400">
            Not imported — a resource has no field for these.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {ignoredHeaders.map((h) => (
              <span
                key={h}
                className="inline-flex items-center rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-500"
              >
                {h}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-3">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <div className="flex items-center gap-3">
          {!canContinue && (
            <span className="text-xs text-amber-600">
              Map required field{requiredMissing.length === 1 ? '' : 's'}: {requiredMissing.join(', ')}
            </span>
          )}
          <Button type="button" onClick={onContinue} disabled={!canContinue}>
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
